// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { Rectangle } from './Geometry';
import { Color, LinearGradientBrush, RectangleEdgeFilter } from './Graphics';
CanvasRenderingContext2D.prototype.DrawLine = function (pen, X1, Y1, X2, Y2) {
    this.save();
    this.beginPath();
    pen.applySettings(this);
    const crispCoef = (this.lineWidth % 2) / 2;
    if (crispCoef)
        this.translate(crispCoef, crispCoef);
    this.moveTo(Math.round(X1), Math.round(Y1));
    this.lineTo(Math.round(X2), Math.round(Y2));
    this.stroke();
    if (crispCoef)
        this.translate(-crispCoef, -crispCoef);
    this.restore();
};
CanvasRenderingContext2D.prototype.DrawLines = function (pen, points) {
    if (!points)
        return;
    const len = points.length;
    if (len < 2)
        return;
    this.beginPath();
    pen.applySettings(this);
    const crispCoef = (this.lineWidth % 2) / 2;
    if (crispCoef)
        this.translate(crispCoef, crispCoef);
    const p0 = points[0];
    this.moveTo(Math.round(p0.X), Math.round(p0.Y));
    for (let i = 1; i < len; i++) {
        const p = points[i];
        this.lineTo(Math.round(p.X), Math.round(p.Y));
    }
    this.stroke();
    if (crispCoef)
        this.translate(-crispCoef, -crispCoef);
};
CanvasRenderingContext2D.prototype.DrawRect = function (pen, X, Y, Width, Height) {
    pen.applySettings(this);
    const crispCoef = (this.lineWidth % 2) / 2;
    if (crispCoef)
        this.translate(crispCoef, crispCoef);
    this.strokeRect(Math.round(X), Math.round(Y), Math.round(Width), Math.round(Height));
    if (crispCoef)
        this.translate(-crispCoef, -crispCoef);
};
CanvasRenderingContext2D.prototype.FillRect = function (fillStyle, X, Y, Width, Height) {
    fillStyle.applySettings(this);
    this.fillRect(Math.round(X), Math.round(Y), Math.round(Width), Math.round(Height));
};
CanvasRenderingContext2D.prototype.FillRectWithRect = function (fillStyle, rect) {
    this.FillRect(fillStyle, rect.X, rect.Y, rect.Width, rect.Height);
};
CanvasRenderingContext2D.prototype.DrawPolygon = function (pen, points) {
    if (!points)
        return;
    const len = points.length;
    if (!len || len < 3)
        return;
    this.beginPath();
    pen.applySettings(this);
    const pStart = points[0];
    this.moveTo(pStart.X, pStart.Y);
    for (let i = 1; i < len; i++) {
        const p = points[i];
        this.lineTo(p.X, p.Y);
    }
    this.closePath();
    this.stroke();
};
CanvasRenderingContext2D.prototype.FillPolygon = function (fillStyle, points) {
    if (!points)
        return;
    const len = points.length;
    if (!len || len < 3)
        return;
    this.beginPath();
    fillStyle.applySettings(this);
    const pStart = points[0];
    this.moveTo(pStart.X, pStart.Y);
    for (let i = 1; i < len; i++) {
        const p = points[i];
        this.lineTo(p.X, p.Y);
    }
    this.closePath();
    this.fill();
};
CanvasRenderingContext2D.prototype.DrawTextInRect = function (text, rectangle, isTrim = false, horizontalAlign = 'center', verticalAlign = 'middle') {
    this.save();
    let measuredText = text;
    if (isTrim && this.GetTextMetrics(measuredText).width > rectangle.Width) {
        const ellipsis = '...';
        while (measuredText !== '' && this.GetTextMetrics(measuredText + ellipsis).width > rectangle.Width) {
            measuredText = measuredText.slice(0, -1);
        }
        measuredText += ellipsis;
    }
    this.textAlign = horizontalAlign;
    this.textBaseline = verticalAlign;
    let xOffset;
    let yOffset;
    switch (horizontalAlign) {
        case 'left':
            xOffset = rectangle.X;
            break;
        case 'center':
            xOffset = rectangle.X + rectangle.Width / 2 + 1;
            break;
        case 'right':
            xOffset = rectangle.X + rectangle.Width;
            break;
    }
    switch (verticalAlign) {
        case 'top':
            yOffset = rectangle.Y;
            break;
        case 'middle':
            yOffset = rectangle.Y + rectangle.Height / 2 + 1;
            break;
        case 'bottom':
            yOffset = rectangle.Y + rectangle.Height;
            break;
    }
    this.moveTo(xOffset, yOffset);
    this.fillText(measuredText, xOffset, yOffset);
    this.restore();
};
CanvasRenderingContext2D.prototype.DrawString = function (text, font, brush, X, Y, textAlign, textBaseline) {
    this.textAlign = textAlign || 'left';
    this.textBaseline = textBaseline || 'top';
    this.save();
    font.applySettings(this);
    brush.applySettings(this);
    this.fillText(text, Math.floor(X), Math.floor(Y));
    this.restore();
};
CanvasRenderingContext2D.prototype.DrawStringInRectNew = function (text, brush, rectangle, isTrim = false) {
    this.save();
    let measuredText = text;
    if (isTrim && this.GetTextSizeNew(measuredText, this.font, true).width > rectangle.Width) {
        measuredText = this.getTrimTextNew(measuredText, this.font, rectangle);
    }
    let xOffset;
    let yOffset;
    switch (this.textAlign) {
        case 'left':
        case 'start':
            xOffset = rectangle.X;
            break;
        case 'center':
            xOffset = rectangle.X + rectangle.Width / 2;
            break;
        case 'end':
        case 'right':
            xOffset = rectangle.X + rectangle.Width;
            break;
    }
    switch (this.textBaseline) {
        case 'top':
            yOffset = rectangle.Y;
            break;
        case 'middle':
            yOffset = rectangle.Y + rectangle.Height / 2 + 1;
            break;
        case 'bottom':
            yOffset = rectangle.Y + rectangle.Height;
            break;
    }
    brush.applySettings(this);
    this.moveTo(xOffset, yOffset);
    this.fillText(measuredText, xOffset, yOffset);
    this.restore();
};
CanvasRenderingContext2D.prototype.DrawMatchStringInRect = function (text, rectangle, matchText, matchBrush, backgroundBrush, isTrim = false) {
    if (!isValidString(text) || !isValidString(matchText)) {
        return;
    }
    const matchIndex = text.toLowerCase().indexOf(matchText.toLowerCase());
    if (matchIndex === -1) {
        return;
    }
    const context = this;
    context.save();
    let matchStartIndex = matchIndex;
    let matchEndIndex = matchIndex + matchText.length;
    let measuredText = text;
    if (isTrim && context.GetTextSizeNew(measuredText, context.font, true).width > rectangle.Width) {
        measuredText = context.getTrimTextNew(measuredText, context.font, rectangle);
        if (matchStartIndex >= measuredText.length - 3) {
            matchStartIndex = measuredText.length - 3;
        }
        if (matchEndIndex >= measuredText.length - 3) {
            matchEndIndex = measuredText.length;
        }
    }
    const beforeText = measuredText.slice(0, matchStartIndex);
    const matchedText = measuredText.slice(matchStartIndex, matchEndIndex);
    const beforeTextWidth = context.measureText(beforeText).width;
    const matchTextWidth = context.measureText(matchedText).width;
    const matchTextRect = rectangle.copy();
    matchTextRect.X += beforeTextWidth;
    matchTextRect.Width = matchTextWidth;
    if (!isNullOrUndefined(backgroundBrush)) {
        backgroundBrush.applySettings(context);
    }
    context.fillRect(matchTextRect.X, matchTextRect.Y, matchTextRect.Width, matchTextRect.Height);
    context.DrawStringInRectNew(matchedText, matchBrush, matchTextRect, false);
    context.restore();
};
/**
 * @deprecated Use DrawStringInRectNew instead.
 */
CanvasRenderingContext2D.prototype.DrawStringInRect = function (text, font, brush, rectangle, isTrim = false, horizontalAlign = 'center', verticalAlign = 'middle', isClip = false) {
    this.save();
    if (isClip) {
        this.beginPath();
        this.rect(rectangle.X, rectangle.Y, rectangle.Width, rectangle.Height);
        this.clip();
    }
    let measuredText = text;
    if (isTrim && this.GetTextSize(measuredText, font, true).width > rectangle.Width) {
        measuredText = this.getTrimText(measuredText, font, rectangle);
    }
    this.textAlign = horizontalAlign;
    this.textBaseline = verticalAlign;
    let xOffset;
    let yOffset;
    switch (horizontalAlign) {
        case 'left':
        case 'start':
            xOffset = rectangle.X;
            break;
        case 'center':
            xOffset = rectangle.X + rectangle.Width / 2;
            break;
        case 'end':
        case 'right':
            xOffset = rectangle.X + rectangle.Width;
            break;
    }
    switch (verticalAlign) {
        case 'top':
            yOffset = rectangle.Y;
            break;
        case 'middle':
            yOffset = rectangle.Y + rectangle.Height / 2 + 1;
            break;
        case 'bottom':
            yOffset = rectangle.Y + rectangle.Height;
            break;
    }
    font.applySettings(this);
    brush.applySettings(this);
    this.moveTo(xOffset, yOffset);
    this.fillText(measuredText, xOffset, yOffset);
    this.restore();
};
CanvasRenderingContext2D.prototype.getTrimTextNew = function (measuredText, font, rectangle) {
    const ellipsis = '...';
    const ellipsisWidth = this.GetTextSizeNew(ellipsis, font, true).width;
    let left = 0;
    let right = measuredText.length;
    let result = '';
    while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const candidate = measuredText.slice(0, mid);
        const candidateWidth = this.GetTextSizeNew(candidate, font, true).width;
        if (candidateWidth + ellipsisWidth <= rectangle.Width) {
            result = candidate;
            left = mid + 1;
        }
        else {
            right = mid - 1;
        }
    }
    measuredText = result + ellipsis;
    return measuredText;
};
/**
 * @deprecated Use DrawStringInRectNew instead.
 */
CanvasRenderingContext2D.prototype.getTrimText = function (measuredText, font, rectangle) {
    const ellipsis = '...';
    const ellipsisWidth = this.GetTextSize(ellipsis, font, true).width;
    let left = 0;
    let right = measuredText.length;
    let result = '';
    while (left <= right) {
        const mid = Math.floor((left + right) / 2);
        const candidate = measuredText.slice(0, mid);
        const candidateWidth = this.GetTextSize(candidate, font, true).width;
        if (candidateWidth + ellipsisWidth <= rectangle.Width) {
            result = candidate;
            left = mid + 1;
        }
        else {
            right = mid - 1;
        }
    }
    measuredText = result + ellipsis;
    return measuredText;
};
CanvasRenderingContext2D.prototype.DrawEllipse = function (pen, x, y, w, h) {
    const kappa = 0.5522848;
    const ox = (w / 2) * kappa; // control point offset horizontal
    const oy = (h / 2) * kappa; // control point offset vertical
    const xe = x + w; // x-end
    const ye = y + h; // y-end
    const xm = x + w / 2; // x-middle
    const ym = y + h / 2; // y-middle
    this.beginPath();
    pen.applySettings(this);
    this.moveTo(x, ym);
    this.bezierCurveTo(x, ym - oy, xm - ox, y, xm, y);
    this.bezierCurveTo(xm + ox, y, xe, ym - oy, xe, ym);
    this.bezierCurveTo(xe, ym + oy, xm + ox, ye, xm, ye);
    this.bezierCurveTo(xm - ox, ye, x, ym + oy, x, ym);
    // this.closePath(); // not used correctly, see comments (use to close off open path)
    this.stroke();
};
CanvasRenderingContext2D.prototype.FillEllipse = function (brush, x, y, w, h, borderPen) {
    const kappa = 0.5522848;
    const ox = (w / 2) * kappa; // control point offset horizontal
    const oy = (h / 2) * kappa; // control point offset vertical
    const xe = x + w; // x-end
    const ye = y + h; // y-end
    const xm = x + w / 2; // x-middle
    const ym = y + h / 2; // y-middle
    this.beginPath();
    brush.applySettings(this);
    this.moveTo(x, ym);
    this.bezierCurveTo(x, ym - oy, xm - ox, y, xm, y);
    this.bezierCurveTo(xm + ox, y, xe, ym - oy, xe, ym);
    this.bezierCurveTo(xe, ym + oy, xm + ox, ye, xm, ye);
    this.bezierCurveTo(xm - ox, ye, x, ym + oy, x, ym);
    // this.closePath(); // not used correctly, see comments (use to close off open path)
    this.fill();
    if (!isNullOrUndefined(borderPen)) {
        this.strokeStyle = borderPen.Color;
        this.stroke();
    }
};
CanvasRenderingContext2D.prototype.DrawArc = function (pen, centerX, centerY, radius, startAngleRad, endAngleRad) {
    this.beginPath();
    pen.applySettings(this);
    const crispCoef = (this.lineWidth % 2) / 2;
    if (crispCoef)
        this.translate(crispCoef, crispCoef);
    this.arc(Math.round(centerX), Math.round(centerY), Math.round(radius), startAngleRad, endAngleRad);
    this.stroke();
    if (crispCoef)
        this.translate(-crispCoef, -crispCoef);
};
CanvasRenderingContext2D.prototype.DrawCurve = function (pen, points) {
    if (!points)
        return;
    const len = points.length;
    if (!len || len < 2)
        return;
    this.beginPath();
    pen.applySettings(this);
    const convPoints = [];
    for (let i = 0; i < len; i++) {
        const point = points[i];
        convPoints[i * 2] = point.X;
        convPoints[i * 2 + 1] = point.Y;
    }
    this.curve(convPoints);
    this.stroke();
};
CanvasRenderingContext2D.prototype.DrawFilledCurve = function (fillStyle, points) {
    if (!points)
        return;
    const len = points.length;
    if (!len || len < 3)
        return;
    this.beginPath();
    fillStyle.applySettings(this);
    const convPoints = [];
    for (let i = 0; i < len - 2; i++) {
        const point = points[i];
        convPoints[i * 2] = point.X;
        convPoints[i * 2 + 1] = point.Y;
    }
    var pStart = points[len - 2];
    this.moveTo(pStart.X, pStart.Y);
    var pStart = points[len - 1];
    this.lineTo(pStart.X, pStart.Y);
    this.curve(convPoints);
    this.fill();
};
CanvasRenderingContext2D.prototype.DrawClosedCurve = function (pen, points) {
    if (!points)
        return;
    const len = points.length;
    if (!len || len < 3)
        return;
    this.beginPath();
    pen.applySettings(this);
    const convPoints = [];
    for (let i = 0; i < len; i++) {
        const point = points[i];
        convPoints[i * 2] = point.X;
        convPoints[i * 2 + 1] = point.Y;
    }
    this.curve(convPoints, 0.5, 25, true);
    this.stroke();
};
CanvasRenderingContext2D.prototype.FillClosedCurve = function (fillStyle, points) {
    if (!points)
        return;
    const len = points.length;
    if (!len || len < 3)
        return;
    this.beginPath();
    fillStyle.applySettings(this);
    const convPoints = [];
    for (let i = 0; i < len; i++) {
        const point = points[i];
        convPoints[i * 2] = point.X;
        convPoints[i * 2 + 1] = point.Y;
    }
    this.curve(convPoints, 0.5, 25, true);
    this.fill();
};
export const TO_RADIANS = Math.PI / 180;
CanvasRenderingContext2D.prototype.RotateByAngle = function (angle) {
    this.rotate(angle * TO_RADIANS);
};
CanvasRenderingContext2D.prototype.DrawRotatedImage = function (image, x, y, angle) {
    if (!image)
        return;
    this.save();
    this.translate(x, y);
    this.rotate(angle * TO_RADIANS);
    this.drawImage(image, -(image.width / 2), -(image.height / 2));
    this.restore();
};
CanvasRenderingContext2D.prototype.GetTextMetrics = function (text, font) {
    this.save();
    if (isValidString(font)) {
        this.font = font;
    }
    const metics = this.measureText(text);
    this.restore();
    return metics;
};
CanvasRenderingContext2D.prototype.GetTextWidth = function (text, font) {
    this.save();
    font.applySettings(this);
    const w = this.measureText(text).width;
    this.restore();
    return w;
};
CanvasRenderingContext2D.prototype.GetTextSizeNew = function (text, font, isActualBounding = true) {
    this.save();
    this.font = font;
    const textMetics = this.measureText(text);
    const width = Math.round(textMetics.width);
    const height = isActualBounding
        ? Math.round(textMetics.actualBoundingBoxAscent + textMetics.actualBoundingBoxDescent)
        : Math.round(textMetics.fontBoundingBoxAscent + textMetics.fontBoundingBoxDescent);
    this.restore();
    return { width, height };
};
/**
 * @deprecated Use GetTextSizeNew instead.
 */
CanvasRenderingContext2D.prototype.GetTextSize = function (text, font, isActualBounding = true) {
    this.save();
    font.applySettings(this);
    const textMetics = this.measureText(text);
    const width = Math.round(textMetics.width);
    const height = isActualBounding
        ? Math.round(textMetics.actualBoundingBoxAscent + textMetics.actualBoundingBoxDescent)
        : Math.round(textMetics.fontBoundingBoxAscent + textMetics.fontBoundingBoxDescent);
    this.restore();
    return { width, height };
};
export const ELLIPSIS = '…'; // '...';
CanvasRenderingContext2D.prototype.ToImage = function () {
    const image = new Image();
    image.src = this.canvas.toDataURL('image/png');
    return image;
};
CanvasRenderingContext2D.prototype.Clear = function () {
    this.clearRect(0, 0, this.width, this.height);
};
CanvasRenderingContext2D.prototype.RenderButton = function (brush, pen, rect) {
    const cpy_rect = rect.copy();
    cpy_rect.Inflate(-2, -2);
    this.RoundRectangle(cpy_rect, 2, brush, pen, RectangleEdgeFilter.All);
};
CanvasRenderingContext2D.prototype.getPixelColor = function (x, y) {
    const data = this.getImageData(x, y, 1, 1).data;
    return Color.CreateColor(data[0], data[1], data[2], data[3] / 255);
};
CanvasRenderingContext2D.prototype.DrawPolyLine = function (pen, polyLine) {
    if (polyLine.lines.length === 0) {
        return;
    }
    this.beginPath();
    pen.applySettings(this);
    const crispCoef = (this.lineWidth % 2) / 2;
    if (crispCoef)
        this.translate(crispCoef, crispCoef);
    const lines = polyLine.lines;
    let i;
    const len = lines.length;
    for (i = 0; i < len; i++) {
        const curLine = lines[i];
        this.moveTo(Math.round(curLine.X1), Math.round(curLine.Y1));
        this.lineTo(Math.round(curLine.X2), Math.round(curLine.Y2));
    }
    this.stroke();
    if (crispCoef)
        this.translate(-crispCoef, -crispCoef);
};
CanvasRenderingContext2D.prototype.DrawPolyRect = function (fillStyle, polyRect, penStyle) {
    if (polyRect.rects.length === 0) {
        return;
    }
    fillStyle.applySettings(this);
    const rects = polyRect.rects;
    let i;
    const len = rects.length;
    for (i = 0; i < len; i++) {
        var curRect = rects[i];
        this.fillRect(Math.round(curRect.X), Math.round(curRect.Y), Math.round(curRect.Width), Math.round(curRect.Height));
        // if (usePen)
        //    this.strokeRect(
        //        Math.round(curRect.X),
        //        Math.round(curRect.Y),
        //        Math.round(curRect.Width-1),
        //        Math.round(curRect.Height));
    }
    const usePen = !isNullOrUndefined(penStyle);
    if (usePen) {
        penStyle.applySettings(this);
        var crispCoef = (this.lineWidth % 2) / 2;
        if (crispCoef)
            this.translate(crispCoef, crispCoef);
    }
    for (i = 0; i < len; i++) {
        var curRect = rects[i];
        // this.fillRect(
        //    Math.round(curRect.X),
        //    Math.round(curRect.Y),
        //    Math.round(curRect.Width),
        //    Math.round(curRect.Height));
        if (usePen) {
            this.strokeRect(Math.round(curRect.X), Math.round(curRect.Y), Math.round(curRect.Width - 1), Math.round(curRect.Height));
        }
    }
    if (usePen) {
        if (crispCoef)
            this.translate(-crispCoef, -crispCoef);
    }
};
CanvasRenderingContext2D.prototype.DrawPolyText = function (polyText, font, brush, textAlign = 'left', textBaseline = 'top') {
    if (polyText.textArray.length === 0) {
        return;
    }
    this.textAlign = textAlign || 'left';
    this.textBaseline = textBaseline || 'top';
    font.applySettings(this);
    brush.applySettings(this);
    const textItems = polyText.textArray;
    let i;
    const len = textItems.length;
    for (i = 0; i < len; i++) {
        const curTextItem = textItems[i];
        this.fillText(curTextItem.Text, Math.floor(curTextItem.X), Math.floor(curTextItem.Y));
    }
};
function getRoundRectPath(rect, radius, edgeFilter = RectangleEdgeFilter.All) {
    const shape = new Path2D();
    const hasRoundRect = typeof shape.roundRect === 'function';
    if (hasRoundRect) {
        const radii = [];
        if (RectangleEdgeFilter.IsTopLeft(edgeFilter))
            radii.push(radius);
        else
            radii.push(0);
        if (RectangleEdgeFilter.IsTopRight(edgeFilter))
            radii.push(radius);
        else
            radii.push(0);
        if (RectangleEdgeFilter.IsBottomRight(edgeFilter))
            radii.push(radius);
        else
            radii.push(0);
        if (RectangleEdgeFilter.IsBottomLeft(edgeFilter))
            radii.push(radius);
        else
            radii.push(0);
        shape.roundRect(rect.X, rect.Y, rect.Width, rect.Height, radii);
        return shape;
    }
    else {
        return getRoundRectPathOldVersion(rect, radius, edgeFilter);
    }
}
//exclude for older browsers that do not support Path2D.roundRect (for example Safari 15)
function getRoundRectPathOldVersion(rect, radius, edgeFilter = RectangleEdgeFilter.All) {
    const rectCopy = rect.copy();
    const { X: x, Y: y, Width: w, Height: h } = rectCopy;
    const r = Math.min(radius, w / 2, h / 2);
    const right = x + w;
    const bottom = y + h;
    const shape = new Path2D();
    const hasTopLeft = RectangleEdgeFilter.IsTopLeft(edgeFilter);
    const hasTopRight = RectangleEdgeFilter.IsTopRight(edgeFilter);
    const hasBottomRight = RectangleEdgeFilter.IsBottomRight(edgeFilter);
    const hasBottomLeft = RectangleEdgeFilter.IsBottomLeft(edgeFilter);
    shape.moveTo(x + (hasTopLeft ? r : 0), y);
    // Top edge
    shape.lineTo(right - (hasTopRight ? r : 0), y);
    if (hasTopRight)
        shape.arcTo(right, y, right, y + r, r);
    else
        shape.lineTo(right, y);
    // Right edge
    shape.lineTo(right, bottom - (hasBottomRight ? r : 0));
    if (hasBottomRight)
        shape.arcTo(right, bottom, right - r, bottom, r);
    else
        shape.lineTo(right, bottom);
    // Bottom edge
    shape.lineTo(x + (hasBottomLeft ? r : 0), bottom);
    if (hasBottomLeft)
        shape.arcTo(x, bottom, x, bottom - r, r);
    else
        shape.lineTo(x, bottom);
    // Left edge
    shape.lineTo(x, y + (hasTopLeft ? r : 0));
    if (hasTopLeft)
        shape.arcTo(x, y, x + r, y, r);
    else
        shape.lineTo(x, y);
    shape.closePath();
    return shape;
}
// #region RoundRectangle Methods.
CanvasRenderingContext2D.prototype.RoundRectangle = function (rect, radius, fillBrush, borderPen, edgeFilter = RectangleEdgeFilter.All) {
    if (!isValidNumber(rect.Width) || rect.Width <= 0 || !isValidNumber(rect.Height) || rect.Height <= 0)
        return;
    const ctx = this;
    ctx.save();
    let strokePath;
    if (!isNullOrUndefined(borderPen)) {
        borderPen.applySettings(ctx);
        const lineWidth = ctx.lineWidth || 0;
        let strokeRect = rect;
        if (lineWidth > 0) {
            const inset = lineWidth / 2;
            strokeRect = new Rectangle(rect.X + inset, rect.Y + inset, rect.Width - lineWidth, rect.Height - lineWidth);
        }
        strokePath = getRoundRectPath(strokeRect, radius, edgeFilter);
        ctx.clip(strokePath);
    }
    if (!isNullOrUndefined(fillBrush)) {
        const shape = getRoundRectPath(rect, radius, edgeFilter);
        fillBrush.applySettings(ctx);
        ctx.fill(shape);
    }
    ctx.restore();
    ctx.save();
    if (!isNullOrUndefined(borderPen)) {
        borderPen.applySettings(ctx);
        ctx.stroke(strokePath);
    }
    ctx.restore();
};
CanvasRenderingContext2D.prototype.RoundRectangleGradient = function (rect, rad, 
/* text, */ col1, col2, 
/* textColor, */ borderPen, 
/* sf, f, */ leftScip, rightScip) {
    const rectX = rect.X;
    const br = new LinearGradientBrush(rectX, 0, rectX + rect.Width, 0, col1, col2);
    this.RoundRectangleFilter(rect, rad, /* text, */ br, /* textColor, */ borderPen, /* sf, f, */ leftScip, rightScip);
};
CanvasRenderingContext2D.prototype.RoundRectangleFilter = function (rect, rad, 
/* text, */ br, 
/* textColor, */ borderPen, leftScip, rightScip) {
    let filter = RectangleEdgeFilter.All;
    if (leftScip)
        filter = RectangleEdgeFilter.All & (RectangleEdgeFilter.BottomRight | RectangleEdgeFilter.TopRight);
    if (rightScip)
        filter = RectangleEdgeFilter.All & (RectangleEdgeFilter.BottomLeft | RectangleEdgeFilter.TopLeft);
    this.RoundRectangle(rect, rad, /* text, */ br, /* textColor, */ borderPen, /* sf, f, */ filter);
};
CanvasRenderingContext2D.prototype.SimpleRoundRectangle = function (X, Y, Width, Height, borderRadius, fillStyle, strokeStyle) {
    if (fillStyle) {
        fillStyle.applySettings(this);
    }
    if (strokeStyle) {
        strokeStyle.applySettings(this);
    }
    this.beginPath();
    this.roundRect(Math.round(X), Math.round(Y), Math.round(Width), Math.round(Height), borderRadius); // borderRadius - a number or list specifying the radii of the circular arc to be used for the corners of the rectangle. The number and order of the radii function in the same way as the border-radius CSS property when width and height are positive
    this.closePath();
    if (fillStyle) {
        this.fill();
    }
    if (strokeStyle) {
        this.stroke();
    }
};
CanvasRenderingContext2D.prototype.DrawCenteredImage = function (image, rect, imageWidth, imageHeight) {
    const imgWidth = imageWidth ? imageWidth : image.width;
    const imgHeight = imageHeight ? imageHeight : image.height;
    const paddingX = this.GetPadding(rect.Width, imgWidth);
    const paddingY = this.GetPadding(rect.Height, imgHeight);
    this.drawImage(image, rect.X + paddingX, rect.Y + paddingY, imgWidth, imgHeight);
};
CanvasRenderingContext2D.prototype.GetPadding = function (parentSize, childSize) {
    const padding = Math.floor((parentSize - childSize) / 2);
    return padding < 0 ? 0 : padding;
};
CanvasRenderingContext2D.prototype.drawRoundRectShadow = function (rect, radius, shadowBrush, shadowStartColor = 'black') {
    if (!isValidNumber(rect.Width) || rect.Width <= 0 || !isValidNumber(rect.Height) || rect.Height <= 0)
        return;
    const ctx = this;
    ctx.save();
    shadowBrush.applySettings(ctx);
    const lineWidth = ctx.lineWidth || 0;
    let strokeRect = rect;
    if (lineWidth > 0) {
        const inset = lineWidth / 2;
        strokeRect = new Rectangle(rect.X + inset, rect.Y + inset, rect.Width - lineWidth, rect.Height - lineWidth);
    }
    const shape = getRoundRectPath(strokeRect, radius, RectangleEdgeFilter.All);
    ctx.fillStyle = shadowStartColor;
    ctx.fill(shape);
    ctx.restore();
};
